import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookOpen, faClock, faTag } from '@/fontawesome'

interface BlogArticle {
  id: string
  title: string
  excerpt: string
  category: string
  date: string
  imageUrl: string
  readTime: string
}

const MOCK_ARTICLES: BlogArticle[] = [
  {
    id: '1',
    title: 'Volatilität als Chance: So nutzen Sie Marktschwankungen für Ihre Trading-Strategie',
    excerpt: 'Volatilität wird oft als Risiko wahrgenommen, doch erfahrene Trader sehen darin Chancen. Erfahren Sie, wie Sie mit der richtigen Strategie von Marktschwankungen profitieren können.',
    category: 'Trading-Strategie',
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=500&fit=crop',
    readTime: '5 min'
  },
  {
    id: '2',
    title: 'Technische Analyse vs. Fundamentalanalyse: Welcher Ansatz passt zu Ihnen?',
    excerpt: 'Die ewige Debatte im Trading - technische oder fundamentale Analyse? Wir beleuchten beide Ansätze und zeigen, wie Sie diese kombinieren können.',
    category: 'Marktanalyse',
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop',
    readTime: '8 min'
  },
  {
    id: '3',
    title: 'Risikomanagement: Die wichtigste Säule erfolgreichen Tradings',
    excerpt: 'Ohne solides Risikomanagement scheitern selbst die besten Strategien. Lernen Sie die Grundlagen des professionellen Risikomanagements kennen.',
    category: 'Risikomanagement',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
    readTime: '6 min'
  },
  {
    id: '4',
    title: 'Optionsstrategien für Einsteiger: Von Covered Calls bis zu Protective Puts',
    excerpt: 'Optionen sind vielseitige Instrumente, die sowohl zur Absicherung als auch zur Spekulation genutzt werden können. Ein praktischer Einstieg in die Welt der Optionen.',
    category: 'Optionen',
    date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&h=500&fit=crop',
    readTime: '10 min'
  },
  {
    id: '5',
    title: 'Psychologie im Trading: Emotionen kontrollieren und diszipliniert bleiben',
    excerpt: 'Erfolg im Trading ist zu 80% Psychologie und nur zu 20% Strategie. Erfahren Sie, wie Sie emotionale Fallstricke vermeiden und mentale Stärke aufbauen.',
    category: 'Trading-Psychologie',
    date: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&h=500&fit=crop',
    readTime: '7 min'
  },
  {
    id: '6',
    title: 'Makroökonomische Indikatoren: Was Trader wissen müssen',
    excerpt: 'Zinsentscheidungen, Inflation, BIP - makroökonomische Daten bewegen die Märkte. Lernen Sie, die wichtigsten Indikatoren richtig zu interpretieren.',
    category: 'Marktanalyse',
    date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&h=500&fit=crop',
    readTime: '9 min'
  }
]

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    'Trading-Strategie': 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    'Marktanalyse': 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    'Risikomanagement': 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    'Optionen': 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    'Trading-Psychologie': 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
  }
  return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300'
}

export default function BlogArticlesSection() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 sm:p-8 shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faBookOpen} className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Trading-Blog
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Wissen und Insights aus der Trading-Welt
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {MOCK_ARTICLES.map((article, index) => (
          <div
            key={article.id}
            className={`group bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden hover:shadow-lg transition-all border border-gray-200 dark:border-gray-600 ${index >= 3 ? 'hidden md:block' : ''}`}
          >
            {/* Image */}
            <div className="relative aspect-video bg-gray-200 dark:bg-gray-600 overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute top-3 left-3">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                  <FontAwesomeIcon icon={faTag} className="w-3 h-3" />
                  {article.category}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2 leading-snug">
                {article.title}
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                {article.excerpt}
              </p>

              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                <span>{formatDate(article.date)}</span>
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                  {article.readTime}
                </span>
              </div>

              <button
                disabled
                className="w-full py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
              >
                Demnächst verfügbar
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 inline-block">
          <p className="text-sm text-indigo-900 dark:text-indigo-300">
            <strong>Coming Soon:</strong> Unser Trading-Blog wird in Kürze mit regelmäßigen Artikeln, Analysen und Trading-Insights verfügbar sein.
          </p>
        </div>
      </div>
    </div>
  )
}
